import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { profileAPI } from '../../services/api'
import { ScaleIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline'

export default function BodyTrackingSummaryCard({ onNavigate }) {
  const { t } = useTranslation()

  const { data: latest, isLoading } = useQuery({
    queryKey: ['latestMeasurement'],
    queryFn: () => profileAPI.getLatestMeasurement(),
    select: (response) => response.data
  })

  if (isLoading) {
    return (
      <div className="bg-surface-container border rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
        <div className="h-8 bg-gray-200 rounded w-3/4" />
      </div>
    )
  }

  const hasData = latest && (latest.weight_kg || latest.body_fat_pct)

  const DeltaIndicator = ({ value, unit = '' }) => {
    if (!value || value === 0) return null
    const isPositive = value > 0
    const Icon = isPositive ? ArrowUpIcon : ArrowDownIcon
    const color = isPositive ? 'text-red-500' : 'text-green-500'
    return (
      <span className={`text-xs ${color} flex items-center`}>
        <Icon className="h-3 w-3 mr-0.5" />
        {Math.abs(value).toFixed(1)}{unit}
      </span>
    )
  }

  return (
    <button
      onClick={() => onNavigate?.('bodyTracking')}
      className="bg-surface-container border rounded-lg p-4 text-left hover:border-green-300 hover:shadow-sm transition-all w-full"
    >
      <div className="flex items-center mb-3">
        <ScaleIcon className="h-5 w-5 text-green-600 mr-2" />
        <h4 className="text-sm font-medium text-gray-700">
          {t('myProgress.bodySummary', 'Body')}
        </h4>
      </div>
      {hasData ? (
        <div className="space-y-1.5">
          {latest.weight_kg && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">{t('profile.weight', 'Weight')}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-gray-900">{latest.weight_kg} kg</span>
                <DeltaIndicator value={latest.weight_delta_kg} unit="kg" />
              </div>
            </div>
          )}
          {latest.body_fat_pct && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">{t('profile.bodyFat', 'Body Fat')}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-gray-900">{latest.body_fat_pct}%</span>
                <DeltaIndicator value={latest.body_fat_delta_pct} unit="%" />
              </div>
            </div>
          )}
          {latest.muscle_mass_kg && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">{t('profile.muscleMass', 'Muscle')}</span>
              <span className="text-sm font-semibold text-gray-900">{latest.muscle_mass_kg} kg</span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-400">{t('myProgress.noMeasurements', 'No measurements yet')}</p>
      )}
    </button>
  )
}
